import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Snackbar,
  Switch,
  Toolbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  Replay as ReplayIcon,
  Shuffle as ShuffleIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { listCards, updateCard, gradeCard, getDueCards, getDeck, renameDeck } from '../../services/flashcardsService';
import MilestoneBar, { useSessionMilestones } from './MilestoneToast';
import '../../styles/flashcard-animations.css';

const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const stripEmojis = (str) =>
  str.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\uFE0F]/gu, '').trim();

const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return trimmed.length > 0 && /^https?:\/\//i.test(trimmed);
};

const FlashcardReview = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const deckId = params.get('deckId') || '';
  const searchText = params.get('search') || '';
  const mode = params.get('mode') || 'all'; // 'all' | 'due'

  const LANG_OPTIONS = [
    { value: '', label: 'Auto' },
    { value: 'en', label: 'EN' },
    { value: 'pt', label: 'PT' },
    { value: 'es', label: 'ES' },
    { value: 'fr', label: 'FR' },
    { value: 'de', label: 'DE' },
    { value: 'it', label: 'IT' },
    { value: 'zh', label: 'ZH' },
    { value: 'ja', label: 'JA' },
    { value: 'ko', label: 'KO' },
    { value: 'ru', label: 'RU' },
    { value: 'ar', label: 'AR' },
    { value: 'hi', label: 'HI' },
  ];

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [cards, setCards] = React.useState([]);
  const [deck, setDeck] = React.useState(null);
  const [frontLang, setFrontLang] = React.useState(localStorage.getItem('sourceLang') || '');
  // Back language state — commented out; one voice for both sides suffices for now.
  // const [backLang, setBackLang] = React.useState(localStorage.getItem('targetLang') || '');

  const storageKey = deckId ? `flashcard_review_${deckId}${searchText ? `_${searchText}` : ''}${mode === 'due' ? '_due' : ''}` : null;

  const [shuffle, setShuffle] = React.useState(() => {
    if (!storageKey) return true;
    try { const s = sessionStorage.getItem(storageKey); if (s) return JSON.parse(s).shuffle ?? true; } catch (_) {}
    return true;
  });
  const [order, setOrder] = React.useState([]);
  const [index, setIndex] = React.useState(0);
  const [revealed, setRevealed] = React.useState(false);

  const [editingOpen, setEditingOpen] = React.useState(false);
  const [editFront, setEditFront] = React.useState('');
  const [editBack, setEditBack] = React.useState('');
  const [editFrontImg, setEditFrontImg] = React.useState('');
  const [editBackImg, setEditBackImg] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '' });

  // Grade animation state
  const [gradeAnim, setGradeAnim] = React.useState('');
  const gradeAnimTimer = React.useRef(null);

  // Session stats for summary
  const [sessionStats, setSessionStats] = React.useState({ hard: 0, good: 0, easy: 0 });
  const [finished, setFinished] = React.useState(false);
  const [grading, setGrading] = React.useState(false);

  // Milestone hook
  const milestones = useSessionMilestones();

  // Refs to avoid re-triggering the load effect when these change
  const userRef = React.useRef(user);
  userRef.current = user;
  const shuffleRef = React.useRef(shuffle);
  shuffleRef.current = shuffle;
  const orderRef = React.useRef(order);
  orderRef.current = order;
  const indexRef = React.useRef(index);
  indexRef.current = index;
  const revealedRef = React.useRef(revealed);
  revealedRef.current = revealed;
  const finishedRef = React.useRef(finished);
  finishedRef.current = finished;
  const gradingRef = React.useRef(grading);
  gradingRef.current = grading;

  const rebuildOrder = React.useCallback((nextCards, shouldShuffle, restoreIndex) => {
    const base = Array.isArray(nextCards) ? nextCards : [];
    const next = shouldShuffle ? shuffleArray(base) : base;
    setOrder(next);
    setIndex(typeof restoreIndex === 'number' ? Math.min(restoreIndex, Math.max(0, next.length - 1)) : 0);
    setRevealed(false);
    setFinished(false);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!deckId) {
        setError('Missing deck');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        // Fetch deck info (for language settings used by TTS)
        getDeck(userRef.current, deckId).then((r) => {
          if (!cancelled && r.data) {
            setDeck(r.data);
            if (r.data.front_language) setFrontLang(r.data.front_language);
            // if (r.data.back_language) setBackLang(r.data.back_language);
          }
        }).catch(() => {});

        let result;
        if (mode === 'due') {
          result = await getDueCards(userRef.current, deckId);
        } else {
          result = await listCards(userRef.current, { deckId, searchText });
        }
        if (cancelled) return;
        if (result.error) {
          setError(result.error);
          setCards([]);
          setOrder([]);
          return;
        }
        setCards(result.data || []);

        // Always try to restore from sessionStorage on load
        let restored = false;
        if (storageKey) {
          try {
            const saved = JSON.parse(sessionStorage.getItem(storageKey));
            if (saved && Array.isArray(saved.orderIds) && saved.orderIds.length > 0) {
              const cardMap = new Map((result.data || []).map((c) => [c.id, c]));
              const restoredOrder = saved.orderIds.map((id) => cardMap.get(id)).filter(Boolean);
              if (restoredOrder.length > 0) {
                setOrder(restoredOrder);
                setIndex(Math.min(saved.index || 0, restoredOrder.length - 1));
                setRevealed(false);
                restored = true;
              }
            }
          } catch (_) {}
        }
        if (!restored) {
          rebuildOrder(result.data || [], shuffleRef.current);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [deckId, searchText, mode, storageKey, rebuildOrder]);

  const current = order[index] || null;

  // Persist progress to sessionStorage whenever index or order changes
  React.useEffect(() => {
    if (!storageKey || order.length === 0) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        index,
        shuffle,
        orderIds: order.map((c) => c.id),
      }));
    } catch (_) {}
  }, [storageKey, index, shuffle, order]);

  React.useEffect(() => {
    if (!editingOpen || !current) return;
    setEditFront(current.front_text || '');
    setEditBack(current.back_text || '');
    setEditFrontImg(current.front_image_url || '');
    setEditBackImg(current.back_image_url || '');
  }, [editingOpen, current]);

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleSaveEdit = async () => {
    if (!current) return;
    if (!editFront.trim()) return;

    setSaving(true);
    try {
      const { data, error: e } = await updateCard(user, current.id, {
        front_text: editFront.trim(),
        back_text: editBack.trim(),
        front_image_url: editFrontImg.trim() || null,
        back_image_url: editBackImg.trim() || null,
      });

      if (e) {
        setSnackbar({ open: true, message: e });
        return;
      }

      const nextCard = data || {
        ...current,
        front_text: editFront.trim(),
        back_text: editBack.trim(),
        front_image_url: editFrontImg.trim() || null,
        back_image_url: editBackImg.trim() || null,
      };

      setCards((prev) => prev.map((c) => (c.id === nextCard.id ? nextCard : c)));
      setOrder((prev) => prev.map((c) => (c.id === nextCard.id ? nextCard : c)));
      setSnackbar({ open: true, message: 'Saved!' });
      setEditingOpen(false);
    } finally {
      setSaving(false);
    }
  };

  // --- Grade a card ---
  const handleGrade = React.useCallback(async (grade) => {
    if (!current || gradingRef.current || finishedRef.current) return;
    setGrading(true);

    // Trigger CSS animation
    if (gradeAnimTimer.current) clearTimeout(gradeAnimTimer.current);
    setGradeAnim(`fc-anim-${grade}`);
    gradeAnimTimer.current = setTimeout(() => setGradeAnim(''), 600);

    try {
      const { data: updatedCard } = await gradeCard(user, current.id, grade, current);

      const merged = updatedCard || { ...current, confidence: grade === 'hard' ? 1 : grade === 'easy' ? 3 : 2 };

      // Update card in both arrays
      setCards((prev) => prev.map((c) => (c.id === merged.id ? merged : c)));
      setOrder((prev) => {
        const next = prev.map((c) => (c.id === merged.id ? merged : c));

        // Re-queue hard cards at the end
        if (grade === 'hard') {
          const remaining = next.slice(indexRef.current + 1);
          const alreadyQueued = remaining.some((c) => c.id === merged.id);
          if (!alreadyQueued) {
            next.push({ ...merged });
          }
        }

        return next;
      });

      // Update session stats
      setSessionStats((prev) => ({ ...prev, [grade]: prev[grade] + 1 }));

      // Check milestones
      milestones.checkMilestones(grade);

      // Advance to next card or finish
      setOrder((latestOrder) => {
        const nextIdx = indexRef.current + 1;
        if (nextIdx >= latestOrder.length) {
          setFinished(true);
          // Clear sessionStorage for this review
          if (storageKey) {
            try { sessionStorage.removeItem(storageKey); } catch (_) {}
          }
        } else {
          setIndex(nextIdx);
        }
        return latestOrder;
      });

      setRevealed(false);
    } catch (err) {
      console.error('Grade error:', err);
      setSnackbar({ open: true, message: 'Could not save grade. Try again.' });
    } finally {
      setGrading(false);
    }
  }, [current, user, milestones, storageKey]);

  const flip = React.useCallback(() => {
    if (!current || finishedRef.current) return;
    setRevealed((r) => !r);
  }, [current]);

  const next = React.useCallback(() => {
    if (!orderRef.current.length || finishedRef.current) return;
    window.speechSynthesis.cancel();
    setIndex((i) => Math.min(orderRef.current.length - 1, i + 1));
    setRevealed(false);
  }, []);

  const prev = React.useCallback(() => {
    if (!orderRef.current.length || finishedRef.current) return;
    window.speechSynthesis.cancel();
    setIndex((i) => Math.max(0, i - 1));
    setRevealed(false);
  }, []);

  const reshuffle = () => {
    window.speechSynthesis.cancel();
    rebuildOrder(cards, true);
  };

  const handleRestart = () => {
    setSessionStats({ hard: 0, good: 0, easy: 0 });
    setFinished(false);
    milestones.resetSession();
    rebuildOrder(cards, shuffle);
  };

  // Keyboard shortcuts
  React.useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.key === 'Escape') {
        e.preventDefault();
        navigate('/flashcards');
        return;
      }

      if (finishedRef.current) return;

      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        flip();
        return;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
        return;
      }

      // Grade shortcuts (only when card is revealed)
      if (revealedRef.current && !gradingRef.current) {
        if (e.key === '1') { e.preventDefault(); handleGrade('hard'); return; }
        if (e.key === '2') { e.preventDefault(); handleGrade('good'); return; }
        if (e.key === '3') { e.preventDefault(); handleGrade('easy'); return; }
      }
    };

    window.addEventListener('keydown', handler, { passive: false });
    return () => window.removeEventListener('keydown', handler);
  }, [flip, next, prev, navigate, handleGrade]);

  const totalGraded = sessionStats.hard + sessionStats.good + sessionStats.easy;

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', mt: { sm: -3.5 } }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar variant="dense">
          <IconButton edge="start" onClick={() => navigate('/flashcards')} aria-label="Back to flashcards">
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ ml: 1, flexGrow: 1 }} />

          {!finished && (
            <>
              {/* Language selectors */}
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 1 }}>
                <VolumeUpIcon
                  sx={{
                    fontSize: 22,
                    color: 'primary.main',
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: '50%',
                    p: 0.4,
                    boxShadow: '0 0 8px 2px rgba(102, 126, 234, 0.25)',
                    '&:hover': {
                      boxShadow: '0 0 12px 4px rgba(102, 126, 234, 0.4)',
                    },
                  }}
                />
                <Select
                  size="small"
                  value={frontLang}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFrontLang(val);
                    if (deck) {
                      renameDeck(user, deckId, { name: deck.name, front_language: val || null }).catch(() => {});
                      setDeck((d) => d ? { ...d, front_language: val || null } : d);
                    }
                    setSnackbar({ open: true, message: 'Voice language saved' });
                  }}
                  displayEmpty
                  renderValue={(v) => `Voice: ${v ? v.toUpperCase() : 'Auto'}`}
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    height: 28,
                    minWidth: 72,
                    '& .MuiSelect-select': { py: 0.25, px: 1 },
                  }}
                >
                  {LANG_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                {/* Back language selector — commented out for now.
                   One voice language for both sides is sufficient.
                   Uncomment if separate front/back voices are needed later.
                <Select
                  size="small"
                  value={backLang}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBackLang(val);
                    if (deck) {
                      renameDeck(user, deckId, { name: deck.name, back_language: val || null }).catch(() => {});
                      setDeck((d) => d ? { ...d, back_language: val || null } : d);
                    }
                    setSnackbar({ open: true, message: 'Back language saved' });
                  }}
                  displayEmpty
                  renderValue={(v) => `B: ${v ? v.toUpperCase() : 'Auto'}`}
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    height: 28,
                    minWidth: 72,
                    '& .MuiSelect-select': { py: 0.25, px: 1 },
                  }}
                >
                  {LANG_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                */}
              </Stack>

              <FormControlLabel
                control={
                  <Switch
                    checked={shuffle}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShuffle(checked);
                      rebuildOrder(cards, checked);
                    }}
                  />
                }
                label="Shuffle"
              />

              <IconButton onClick={() => setEditingOpen(true)} aria-label="Edit card" disabled={!current}>
                <EditIcon />
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 1.5, sm: 1.5 } }}>
        {loading && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography color="text.secondary">Loading…</Typography>
          </Paper>
        )}

        {!loading && error && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography color="error" sx={{ fontWeight: 800, mb: 1 }}>
              Review unavailable
            </Typography>
            <Typography color="text.secondary">{error}</Typography>
          </Paper>
        )}

        {!loading && !error && order.length === 0 && !finished && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography sx={{ fontWeight: 800, mb: 1 }}>
              {mode === 'due' ? 'No cards due right now' : 'No cards to review'}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {mode === 'due'
                ? 'All caught up! Come back later when cards are due for review.'
                : 'Create or import flashcards in this deck first.'}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/flashcards')}>
              Back to Flashcards
            </Button>
          </Paper>
        )}

        {/* --- Session Summary --- */}
        {!loading && !error && finished && (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
              Session Complete!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              You reviewed {totalGraded} card{totalGraded !== 1 ? 's' : ''}
            </Typography>

            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
              {sessionStats.easy > 0 && (
                <Chip
                  label={`${sessionStats.easy} Easy`}
                  sx={{ fontWeight: 700, bgcolor: 'success.main', color: 'success.contrastText' }}
                />
              )}
              {sessionStats.good > 0 && (
                <Chip
                  label={`${sessionStats.good} Good`}
                  sx={{ fontWeight: 700, bgcolor: 'warning.main', color: 'warning.contrastText' }}
                />
              )}
              {sessionStats.hard > 0 && (
                <Chip
                  label={`${sessionStats.hard} Hard`}
                  sx={{ fontWeight: 700, bgcolor: 'error.main', color: 'error.contrastText' }}
                />
              )}
            </Stack>

            {totalGraded > 0 && (
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mb: 3,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #4caf50, #ff9800, #f44336)',
                    borderRadius: 4,
                  },
                }}
              />
            )}

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Come back later — your cards will be scheduled for review based on how well you knew them.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<ReplayIcon />}
                onClick={handleRestart}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)' },
                }}
              >
                Review Again
              </Button>
              <Button variant="outlined" onClick={() => navigate('/flashcards')}>
                Back to Flashcards
              </Button>
            </Stack>
          </Paper>
        )}

        {/* --- Active Card --- */}
        {!loading && !error && current && !finished && (
          <Box sx={{ position: 'relative' }}>
            {/* Milestone progress bar */}
            <MilestoneBar
              gradeCount={milestones.gradeCount}
              orbAnim={milestones.orbAnim}
              countPopKey={milestones.countPopKey}
              milestoneMsg={milestones.milestoneMsg}
              barFlashKey={milestones.barFlashKey}
              sparksKey={milestones.sparksKey}
              ringsKey={milestones.ringsKey}
              nextMilestone={milestones.nextMilestone}
            />

            <Paper
              variant="outlined"
              className={gradeAnim}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                position: 'relative',
                overflow: 'visible',
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <IconButton onClick={reshuffle} aria-label="Reshuffle" disabled={!order.length} size="small">
                      <ShuffleIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                      {index + 1} / {order.length}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    Space = flip · 1/2/3 = grade · ←/→ = browse
                  </Typography>
                </Stack>

                <Divider />

                {/* Navigation row */}
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                  <Button startIcon={<ChevronLeftIcon />} onClick={prev} disabled={index <= 0} size="small">
                    Prev
                  </Button>
                  <Button variant="contained" onClick={flip} size="small">
                    Flip
                  </Button>
                  <Button endIcon={<ChevronRightIcon />} onClick={next} disabled={index >= order.length - 1} size="small">
                    Next
                  </Button>
                </Stack>

                {/* Card content */}
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={flip}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') flip();
                  }}
                  sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    py: { xs: 2, sm: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 2,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="overline" color="text.secondary">
                      {revealed ? 'Back' : 'Front'}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const raw = revealed ? (current.back_text || '') : (current.front_text || '');
                        const text = stripEmojis(raw);
                        if (!text) return;
                        if (window.speechSynthesis.speaking) { window.speechSynthesis.cancel(); return; }
                        const langMap = { zh: 'zh-CN', pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', ja: 'ja-JP', ko: 'ko-KR', ru: 'ru-RU', ar: 'ar-SA', hi: 'hi-IN' };
                        // Single voice language for both sides (back selector commented out)
                        const lang = frontLang || localStorage.getItem('sourceLang') || 'auto';
                        const utterance = new SpeechSynthesisUtterance(text);
                        if (lang && lang !== 'auto') utterance.lang = langMap[lang] || lang;
                        window.speechSynthesis.speak(utterance);
                      }}
                      sx={{
                        p: 0.6,
                        color: 'primary.main',
                        bgcolor: 'rgba(102, 126, 234, 0.1)',
                        boxShadow: '0 0 8px 2px rgba(102, 126, 234, 0.25)',
                        animation: 'fc-speaker-glow 2s ease-in-out infinite',
                        '&:hover': {
                          bgcolor: 'rgba(102, 126, 234, 0.2)',
                          boxShadow: '0 0 14px 4px rgba(102, 126, 234, 0.4)',
                        },
                        '@keyframes fc-speaker-glow': {
                          '0%, 100%': { boxShadow: '0 0 8px 2px rgba(102, 126, 234, 0.2)' },
                          '50%': { boxShadow: '0 0 14px 4px rgba(102, 126, 234, 0.4)' },
                        },
                      }}
                      aria-label="Speak text"
                    >
                      <VolumeUpIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                  </Stack>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      letterSpacing: '-0.02em',
                      wordBreak: 'break-word',
                    }}
                  >
                    {revealed ? (current.back_text || '—') : current.front_text}
                  </Typography>

                  {revealed && isValidImageUrl(current.back_image_url) && (
                    <Box
                      key={`back-${current.id}-${revealed}`}
                      component="img"
                      src={current.back_image_url.trim()}
                      alt=""
                      onError={(e) => { e.target.style.display = 'none'; }}
                      sx={{
                        width: '100%',
                        maxWidth: 560,
                        maxHeight: 280,
                        objectFit: 'contain',
                        borderRadius: 2,
                        bgcolor: 'background.default',
                      }}
                    />
                  )}

                  {!revealed && isValidImageUrl(current.front_image_url) && (
                    <Box
                      key={`front-${current.id}-${revealed}`}
                      component="img"
                      src={current.front_image_url.trim()}
                      alt=""
                      onError={(e) => { e.target.style.display = 'none'; }}
                      sx={{
                        width: '100%',
                        maxWidth: 560,
                        maxHeight: 280,
                        objectFit: 'contain',
                        borderRadius: 2,
                        bgcolor: 'background.default',
                      }}
                    />
                  )}
                </Box>

                {/* Grade buttons — only visible when card is revealed */}
                {revealed && (
                  <>
                    <Divider />
                    <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ pt: 0.5 }}>
                      <Button
                        variant="contained"
                        disabled={grading}
                        onClick={() => handleGrade('hard')}
                        sx={{
                          bgcolor: 'error.main',
                          color: 'error.contrastText',
                          fontWeight: 700,
                          minWidth: 90,
                          '&:hover': { bgcolor: 'error.dark' },
                        }}
                      >
                        Hard (1)
                      </Button>
                      <Button
                        variant="contained"
                        disabled={grading}
                        onClick={() => handleGrade('good')}
                        sx={{
                          bgcolor: 'warning.main',
                          color: 'warning.contrastText',
                          fontWeight: 700,
                          minWidth: 90,
                          '&:hover': { bgcolor: 'warning.dark' },
                        }}
                      >
                        Good (2)
                      </Button>
                      <Button
                        variant="contained"
                        disabled={grading}
                        onClick={() => handleGrade('easy')}
                        sx={{
                          bgcolor: 'success.main',
                          color: 'success.contrastText',
                          fontWeight: 700,
                          minWidth: 90,
                          '&:hover': { bgcolor: 'success.dark' },
                        }}
                      >
                        Easy (3)
                      </Button>
                    </Stack>
                  </>
                )}
              </Stack>
            </Paper>
          </Box>
        )}
      </Container>

      <Dialog open={editingOpen} onClose={() => setEditingOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>Edit card</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Front"
              value={editFront}
              onChange={(e) => setEditFront(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              autoFocus
            />
            <TextField
              label="Back"
              value={editBack}
              onChange={(e) => setEditBack(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Front image URL (optional)"
              value={editFrontImg}
              onChange={(e) => setEditFrontImg(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Back image URL (optional)"
              value={editBackImg}
              onChange={(e) => setEditBackImg(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving || !editFront.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={closeSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
};

export default FlashcardReview;
